import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Check,
  CircleAlert,
  Landmark,
  Pencil,
  Save,
  Sparkles,
  X,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useToast } from '../../Utils/toastContext';

import AddressAutocomplete from '../../Components/addressInput/address-input'; // ⬅️ adjust path if it lives elsewhere
const BASE_URL = process.env.REACT_APP_BASE_URL;

// showToast is already imported/available in your project — used when in scope,
// otherwise it just logs (no extra import needed).

const BRAND = '#375AE7';

const GET_BUSINESS_URL = '/api/v1/agency/my-business';
const PUT_ENTITY_URL = '/api/v1/agency/my-business/entity';
const PUT_AUSTRAC_URL = '/api/v1/agency/my-business/austrac';
const DEFAULT_AUSTRAC_MESSAGE =
  'Add the reporting entity number and confirmation date to complete this section.';

const fieldShell =
  'group relative rounded-2xl border border-black/10 bg-white px-3.5 pb-2.5 pt-5 transition duration-300 ease-out hover:border-[#375AE7]/40 hover:shadow-[0_14px_32px_-24px_rgba(55,90,231,0.55)] focus-within:border-[#375AE7] focus-within:shadow-[0_16px_36px_-22px_rgba(55,90,231,0.5)] focus-within:ring-4 focus-within:ring-[#375AE7]/12';
const controlClass =
  'h-8 w-full cursor-text bg-transparent text-[15px] font-medium text-black outline-none placeholder:font-normal placeholder:text-black/35';
const readOnlyControlClass =
  'h-8 w-full cursor-default bg-transparent text-[15px] font-medium text-black outline-none';
/* AddressAutocomplete ships its own border/padding/shadow — strip them so the
   input sits inside the FloatingField shell exactly like the other controls. */
const autocompleteControlClass =
  '!h-8 !w-full !cursor-text !rounded-none !border-0 !bg-transparent !p-0 !text-[15px] !font-medium !text-black !shadow-none !outline-none placeholder:!font-normal placeholder:!text-black/35';

const entityTypes = [
  { value: 'proprietary', label: 'Proprietary company Pty Ltd' },
  { value: 'public', label: 'Public company Ltd' },
];

const emptyForm = {
  legalName: '',
  entityType: '',
  entityTypeLabel: '',
  abn: '',
  acn: '',
  registeredAddress: '',
  contactEmail: '',
  austracNumber: '',
  confirmedDate: '',
};

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const pad = (n) => String(n).padStart(2, '0');

/* "2026-09-01" → local Date (keeps 01/09 in every timezone) */
const parseApiDate = (value) => {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (match)
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d;
};

/* Date → "2026-09-01" using LOCAL parts so the day never shifts */
const toApiDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/* API entity_type ({id,label} | string) → the option used by the read-only field */
const resolveEntityType = (raw) => {
  const apiLabel = typeof raw === 'string' ? raw : raw?.label;
  if (!apiLabel) return { value: '', label: '' };
  const match = entityTypes.find(
    (option) =>
      option.label === apiLabel ||
      option.label.includes(apiLabel) ||
      apiLabel.includes(option.label),
  );
  return match ? match : { value: apiLabel, label: apiLabel };
};

/* Free-text address string → the structured address object the PUT expects.
   Anything we can't parse falls back to the previous stored value. */
const STREET_TYPE =
  /^(st|street|rd|road|ave|avenue|hwy|highway|pde|parade|dr|drive|pl|place|cres|crescent|ct|court|ln|lane|way|blvd|boulevard|tce|terrace|cl|close|gr|grove)$/i;
const UNIT_WORD = /^(level|lvl|unit|suite|shop|floor|apt|apartment|po|gpo)$/i;

const AU_STATES = [
  'new south wales',
  'nsw',
  'victoria',
  'vic',
  'queensland',
  'qld',
  'south australia',
  'sa',
  'western australia',
  'wa',
  'tasmania',
  'tas',
  'northern territory',
  'nt',
  'australian capital territory',
  'act',
];

/* Drops a trailing state ("NSW", "New South Wales") from suburb tokens. */
const stripState = (tokens) => {
  for (let len = 3; len >= 1; len -= 1) {
    if (tokens.length <= len) continue; // keep at least one suburb word
    if (AU_STATES.includes(tokens.slice(-len).join(' ').toLowerCase())) {
      tokens.splice(-len, len);
      return true;
    }
  }
  return false;
};

const parseAddress = (raw, previous = {}) => {
  const text = String(raw ?? '').trim();
  if (!text) return null;

  const address = {
    building_name: previous.building_name ?? null,
    flat_unit_number: null,
    street_number: null,
    street_name: null,
    street_type: null,
    suburb: null,
    address_line_1: previous.address_line_1 ?? null,
    address_line_2: previous.address_line_2 ?? null,
    address_line_3: previous.address_line_3 ?? null,
    city_id: previous.city_id ?? null,
    state_id: previous.state_id ?? null,
    country_id: previous.country_id ?? null,
    zip_code: null,
    google_address: text,
  };

  const parts = text
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return address;

  // Pick the suburb/state/postcode segment: the LAST part holding a postcode,
  // otherwise the last part that isn't a trailing country (e.g. "Australia").
  let tailIndex = -1;
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (/\b\d{3,5}\b/.test(parts[i])) {
      tailIndex = i;
      break;
    }
  }
  if (tailIndex === -1) {
    tailIndex = parts.length - 1;
    if (parts.length > 1 && !/\d/.test(parts[parts.length - 1])) {
      tailIndex = parts.length - 2;
    }
  }

  // tail: "Sydney New South Wales 2000" → suburb / (state) / postcode
  const tailTokens = parts.splice(tailIndex, 1)[0].split(/\s+/);
  if (/^\d{3,5}$/.test(tailTokens[tailTokens.length - 1] || '')) {
    address.zip_code = tailTokens.pop();
  }
  stripState(tailTokens); // "NSW" / "New South Wales" — not part of the suburb
  address.suburb = tailTokens.join(' ') || null;

  // optional unit prefix: "Level 12, 1 Bligh Street, ..."
  const firstToken = (parts[0] || '').split(/\s+/)[0] || '';
  if (parts.length > 1 && UNIT_WORD.test(firstToken)) {
    address.flat_unit_number = parts.shift();
  }

  // street segment: "1 Bligh Street" → number / name / type
  const streetTokens = (parts[0] || '').split(/\s+/);
  if (/^\d+[A-Za-z]?(-\d+)?$/.test(streetTokens[0] || '')) {
    address.street_number = streetTokens.shift();
  }
  if (STREET_TYPE.test(streetTokens[streetTokens.length - 1] || '')) {
    address.street_type = streetTokens.pop();
  }
  address.street_name = streetTokens.join(' ') || null;

  return address;
};

/* AddressAutocomplete selection ({ full, street, route, city, state, zip, country })
   → the structured registered_address object the PUT expects. */
const buildAddressPayload = (addr, previous = {}) => {
  if (!addr) return null;

  const streetLine = [addr.street, addr.route].filter(Boolean).join(' ').trim();
  const text =
    addr.full ||
    [streetLine, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');

  const address = parseAddress(text, previous);

  // Prefer the structured values returned by Mapbox over string parsing
  if (addr.city) address.suburb = addr.city;
  if (addr.zip) address.zip_code = addr.zip;
  address.google_address = addr.full || text;

  return address;
};

/* PUT helper — same flow as your updateApplication() (token, 401, message) */
const PutAPIcalls = async (url, body, navigate) => {
  const token = localStorage.getItem('jwtToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('jwtToken');
        navigate('/login');
        throw new Error('Session expired. Please log in again.');
      }
      const errorData = await response.json().catch(() => ({
        message: 'An unexpected error occurred',
      }));
      throw new Error(errorData.message || 'Something went wrong');
    }

    return await response.json();
  } catch (error) {
    console.error('API Call Error:', error);
    throw new Error(
      typeof error.message === 'string' && error.message
        ? error.message
        : 'Network error, please try again later',
    );
  }
};

/* Full API payload → the flat form the UI edits */
const mapPayloadToForm = (payload) => {
  const sections = Array.isArray(payload?.sections) ? payload.sections : [];
  const entityFields = sections.find((s) => s.key === 'entity')?.fields || {};
  const austracFields = sections.find((s) => s.key === 'austrac')?.fields || {};
  const entityType = resolveEntityType(entityFields.entity_type?.value);

  return {
    legalName: entityFields.registered_legal_name?.value ?? '',
    entityType: entityType.value,
    entityTypeLabel: entityType.label,
    abn: entityFields.abn?.value ?? '',
    acn: entityFields.acn?.value ?? '',
    registeredAddress: entityFields.registered_address?.value ?? '',
    contactEmail: entityFields.primary_contact_email?.value ?? '',
    austracNumber: austracFields.reporting_entity_number?.value ?? '',
    confirmedDate: parseApiDate(austracFields.confirmed_date?.value),
  };
};

/* ------------------------------------------------------------------ *
 * UI pieces (unchanged styling)
 * ------------------------------------------------------------------ */

function FloatingField({ id, label, hint, error, children }) {
  return (
    <div className="min-w-0">
      <div
        className={`${fieldShell} ${error ? 'border-red-300 focus-within:border-red-500 focus-within:ring-red-500/10' : ''}`}
      >
        <label
          htmlFor={id}
          className="pointer-events-none absolute left-3.5 top-2 text-[10px] font-bold uppercase tracking-[0.1em] text-black/40 transition duration-300 group-hover:text-[#375AE7] group-focus-within:text-[#375AE7]"
        >
          {label}
        </label>
        {children}
      </div>
      {error ? (
        <p className="mt-1.5 animate-[mbFade_.25s_ease] text-xs font-medium text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-[11px] leading-4 text-black/40">{hint}</p>
      ) : null}
    </div>
  );
}

function Section({
  index,
  title,
  icon: Icon,
  complete,
  delay,
  action,
  badgeLabel,
  children,
}) {
  return (
    <section
      className="overflow-hidden rounded-[24px] border border-black/[0.08] bg-white shadow-[0_20px_48px_-32px_rgba(0,0,0,0.28)]"
      style={{ animation: `mbRise .55s ease both ${delay || '0s'}` }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-black/[0.06] px-5 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span
            className="grid h-10 w-10 place-items-center rounded-2xl text-white"
            style={{ background: BRAND }}
          >
            <Icon className="h-4 w-4 animate-[mbFloat_3s_ease-in-out_infinite]" />
          </span>
          <div>
            <p
              className="text-[10px] font-bold uppercase tracking-[0.16em]"
              style={{ color: BRAND }}
            >
              0{index}
            </p>
            <h2 className="text-base font-semibold tracking-[-0.03em] text-black">
              {title}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {action}
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] ${
              complete
                ? 'bg-black text-white'
                : 'bg-red-50 text-red-700 ring-1 ring-red-200'
            }`}
          >
            {complete ? (
              <Check className="h-3 w-3" strokeWidth={3} />
            ) : (
              <CircleAlert className="h-3 w-3" />
            )}
            {badgeLabel || (complete ? 'Complete' : 'Action needed')}
          </span>
        </div>
      </div>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * Screen
 * ------------------------------------------------------------------ */

export function MyBusinessScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [savingEntity, setSavingEntity] = useState(false);
  const [savingAustrac, setSavingAustrac] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [saved, setSaved] = useState(emptyForm);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  // Clicking a suggestion can fire onSelect(null) from the blur handler right
  // after onSelect(addr); this guard keeps the real pick.
  const lastAddressPickRef = useRef(0);
  const [detailsDraft, setDetailsDraft] = useState({
    registeredAddress: null, // selected AddressAutocomplete object (null = unchanged)
    addressTouched: false,
    contactEmail: '',
  });

  const sections = useMemo(
    () => (Array.isArray(data?.sections) ? data.sections : []),
    [data],
  );
  const entitySection = sections.find((s) => s.key === 'entity') || null;
  const austracSection = sections.find((s) => s.key === 'austrac') || null;
  const entityFields = entitySection?.fields || {};
  const austracFields = austracSection?.fields || {};

  const canEdit = data?.can_edit !== false;

  const applyPayload = useCallback((payload) => {
    const nextForm = mapPayloadToForm(payload);
    setData(payload ?? null);
    setForm(nextForm);
    setSaved(nextForm); // resets the dirty flags from the server truth
  }, []);

  const getMyBusiness = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await GetAPIcalls(GET_BUSINESS_URL);
      applyPayload(response?.data ?? response ?? null);
    } catch (err) {
      setData(null);
      showToast(err?.message || 'Could not load business details', 'error');
      setLoadError(err?.message || 'Could not load business details');
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    getMyBusiness();
  }, [getMyBusiness]);
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
          status: response.status,
        }));

        // Ensure errorData.message is extracted properly
        const errorMessage =
          typeof errorData === 'object' && errorData.message
            ? errorData.message
            : `Error ${response.status}: ${response.statusText}`;

        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const austracDirty =
    form.austracNumber !== saved.austracNumber ||
    JSON.stringify(form.confirmedDate) !== JSON.stringify(saved.confirmedDate);
  const austracMissing = !form.austracNumber.trim() || !form.confirmedDate;
  const selectedEntityType =
    entityTypes.find((option) => option.value === form.entityType)?.label ||
    form.entityTypeLabel ||
    form.entityType;

  const sectionComplete = (section, fallback) => {
    if (!section) return fallback;
    if (typeof section.is_complete === 'boolean') return section.is_complete;
    if (section.badge?.key) return section.badge.key === 'complete';
    return fallback;
  };

  const entityComplete = sectionComplete(entitySection, true);
  const austracComplete = sectionComplete(austracSection, !austracMissing);
  const austracNotice = austracMissing
    ? austracSection?.message ||
      austracFields.reporting_entity_number?.helper_text ||
      DEFAULT_AUSTRAC_MESSAGE
    : null;

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openDetailsModal = () => {
    setDetailsDraft({
      registeredAddress: null,
      addressTouched: false,
      contactEmail: form.contactEmail,
    });
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    if (savingEntity) return;
    setDetailsModalOpen(false);
  };

  /* PUT /api/v1/agency/my-business/entity — only changed fields are sent */
  const saveDetails = async () => {
    const nextEmail = detailsDraft.contactEmail.trim();
    const selectedAddress = detailsDraft.registeredAddress; // null = unchanged
    const prevEmail = entityFields.primary_contact_email?.value ?? '';
    const prevAddress = entityFields.registered_address?.value ?? '';

    const body = {};
    if (nextEmail !== prevEmail) {
      if (!nextEmail) {
        showToast('Primary contact email cannot be empty.', 'error');
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
        showToast('Enter a valid primary contact email.', 'error');
        return;
      }
      body.primary_contact_email = nextEmail;
    }

    if (detailsDraft.addressTouched) {
      const nextAddress = (selectedAddress?.full || '').trim();
      if (!nextAddress) {
        showToast('Please select an address from the suggestions.', 'error');
        return;
      }
      if (nextAddress !== prevAddress) {
        body.registered_address = buildAddressPayload(
          selectedAddress,
          entityFields.registered_address?.address || {},
        );
      }
    }

    if (!Object.keys(body).length) {
      setDetailsModalOpen(false);
      showToast('No changes to save.', 'error');
      return;
    }

    setSavingEntity(true);
    try {
      const response = await PutAPIcalls(PUT_ENTITY_URL, body, navigate);
      applyPayload(response?.data ?? null);
      setDetailsModalOpen(false);
      showToast(response?.message || 'Business details updated', 'success');
    } catch (err) {
      showToast(err?.message || 'Could not save business details', 'error');
    } finally {
      setSavingEntity(false);
    }
  };

  /* PUT /api/v1/agency/my-business/austrac — null clears a value */
  const handleSaveAustrac = async () => {
    const body = {
      reporting_entity_number: form.austracNumber.trim() || null,
      confirmed_date: toApiDate(form.confirmedDate),
    };

    setSavingAustrac(true);
    try {
      const response = await PutAPIcalls(PUT_AUSTRAC_URL, body, navigate);
      applyPayload(response?.data ?? null);
      showToast(response?.message || 'AUSTRAC details saved', 'success');
    } catch (err) {
      showToast(err?.message || 'Could not save AUSTRAC details', 'error');
    } finally {
      setSavingAustrac(false);
    }
  };

  const handleDiscardAustrac = () => {
    setForm((current) => ({
      ...current,
      austracNumber: saved.austracNumber,
      confirmedDate: saved.confirmedDate,
    }));
  };

  return (
    <div className="w-full pb-8 text-black lg:min-w-[78vw] lg:max-w-[80vw]">
      <style>{`
        @keyframes mbRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes mbFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mbFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
        @keyframes mbSweep { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; }
        }
      `}</style>

      <header
        className="relative mb-7 overflow-hidden rounded-[28px] border border-black/[0.08] bg-white p-6 sm:p-8"
        style={{ animation: 'mbRise .5s ease both' }}
      >
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span
              className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white"
              style={{ background: BRAND }}
            >
              <Sparkles className="h-5 w-5 animate-[mbFloat_2.8s_ease-in-out_infinite]" />
            </span>
            <div>
              <h1 className="mt-1 text-3xl font-semibold tracking-[-0.05em] text-black sm:text-[34px]">
                My Business
              </h1>
              <span
                className="mt-2 block h-[2px] w-16 origin-left rounded-full"
                style={{
                  background: BRAND,
                  animation: 'mbSweep .6s ease .15s both',
                }}
              />
              <p className="mt-3 max-w-lg text-sm leading-6 text-black/55">
                Keep your business and AUSTRAC details current.
              </p>
            </div>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="space-y-5" aria-busy="true">
          {[0, 1].map((row) => (
            <div
              key={row}
              className="overflow-hidden rounded-[24px] border border-black/[0.08] bg-white p-5 shadow-[0_20px_48px_-32px_rgba(0,0,0,0.28)] sm:p-6"
            >
              <div className="animate-pulse space-y-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-2xl bg-black/[0.06]" />
                  <div className="space-y-2">
                    <div className="h-2 w-10 rounded bg-black/[0.06]" />
                    <div className="h-3 w-28 rounded bg-black/[0.08]" />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[0, 1, 2, 3].map((cell) => (
                    <div
                      key={cell}
                      className="h-[62px] rounded-2xl border border-black/10 bg-black/[0.02]"
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {!loading && loadError ? (
        <div className="mb-5 flex items-start gap-2 rounded-[24px] border border-red-200 bg-red-50 px-4 py-3.5 text-xs leading-5 text-red-800">
          <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
          <p className="mr-auto">{loadError}</p>
          <button
            type="button"
            onClick={getMyBusiness}
            className="h-8 shrink-0 rounded-xl border border-red-200 bg-white px-3 text-xs font-semibold text-red-700 transition hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      ) : null}

      {!loading ? (
        <div className="space-y-5">
          <Section
            index="1"
            title={entitySection?.label || 'Entity'}
            icon={Building2}
            complete={entityComplete}
            delay="0.05s"
            badgeLabel={entitySection?.badge?.label}
            action={
              <button
                type="button"
                onClick={openDetailsModal}
                disabled={!canEdit}
                title={
                  canEdit
                    ? undefined
                    : 'Only a business owner can change these details'
                }
                className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-black/10 bg-white px-3 text-[11px] font-bold uppercase tracking-[0.06em] text-black transition hover:border-[#375AE7]/40 hover:bg-[#375AE7]/5 hover:text-[#375AE7] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-black/10 disabled:hover:bg-white disabled:hover:text-black"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit details
              </button>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <FloatingField id="legalName" label="Registered legal name">
                <input
                  id="legalName"
                  value={form.legalName}
                  readOnly
                  className={readOnlyControlClass}
                />
              </FloatingField>

              <FloatingField id="entityType" label="Type of entity">
                <div
                  id="entityType"
                  className={`${readOnlyControlClass} flex items-center`}
                >
                  {selectedEntityType}
                </div>
              </FloatingField>

              <FloatingField id="abn" label="ABN">
                <input
                  id="abn"
                  value={form.abn}
                  readOnly
                  className={readOnlyControlClass}
                />
              </FloatingField>

              <FloatingField id="acn" label="ACN">
                <input
                  id="acn"
                  value={form.acn}
                  readOnly
                  className={readOnlyControlClass}
                />
              </FloatingField>

              <FloatingField id="registeredAddress" label="Registered address">
                <input
                  id="registeredAddress"
                  value={form.registeredAddress}
                  readOnly
                  className={readOnlyControlClass}
                />
              </FloatingField>

              <FloatingField id="contactEmail" label="Primary contact email">
                <input
                  id="contactEmail"
                  type="email"
                  value={form.contactEmail}
                  readOnly
                  className={readOnlyControlClass}
                />
              </FloatingField>
            </div>
          </Section>

          <Section
            index="2"
            title={austracSection?.label || 'AUSTRAC'}
            icon={Landmark}
            complete={austracComplete}
            delay="0.12s"
            badgeLabel={austracSection?.badge?.label}
          >
            {austracNotice ? (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-xs leading-5 text-red-800">
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
                {austracNotice}
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2">
              <FloatingField
                id="austracNumber"
                label="Reporting entity number"
                hint={
                  austracMissing
                    ? undefined
                    : austracFields.reporting_entity_number?.helper_text
                }
                error={
                  austracMissing
                    ? austracFields.reporting_entity_number?.helper_text ||
                      'Required for AUSTRAC reporting entities.'
                    : undefined
                }
              >
                <input
                  id="austracNumber"
                  value={form.austracNumber}
                  onChange={(event) =>
                    update('austracNumber', event.target.value)
                  }
                  disabled={!canEdit}
                  placeholder="e.g. 100123456"
                  className={controlClass}
                />
              </FloatingField>

              <FloatingField id="austracDate" label="Confirmed date">
                <DatePicker
                  selected={form.confirmedDate || null}
                  onChange={(date) => update('confirmedDate', date)}
                  dateFormat="dd/MM/yyyy"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="scroll"
                  yearDropdownItemNumber={100}
                  scrollableYearDropdown
                  portalId="mb-datepicker-portal"
                  placeholderText="Select date"
                  wrapperClassName="w-full"
                  disabled={!canEdit}
                  className={`${controlClass} cursor-pointer`}
                />
              </FloatingField>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-black/[0.08] pt-4">
              <p
                className={`mr-auto text-xs font-medium ${austracDirty ? 'text-black' : 'text-black/40'}`}
              >
                {austracDirty
                  ? 'Unsaved AUSTRAC edits'
                  : 'All AUSTRAC changes saved'}
              </p>
              <button
                type="button"
                disabled={!austracDirty || savingAustrac}
                onClick={handleDiscardAustrac}
                className="inline-flex h-10 cursor-pointer items-center rounded-xl px-4 text-sm font-semibold text-black/70 transition hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Discard
              </button>
              <button
                type="button"
                disabled={!austracDirty || savingAustrac}
                onClick={handleSaveAustrac}
                className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-black/20"
                style={{
                  background:
                    austracDirty && !savingAustrac ? BRAND : undefined,
                }}
              >
                <Save className="h-4 w-4" />
                {savingAustrac ? 'Saving…' : 'Save AUSTRAC changes'}
              </button>
            </div>
          </Section>
        </div>
      ) : null}

      {detailsModalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
              role="presentation"
              onMouseDown={closeDetailsModal}
            >
              <div
                className="w-full max-w-lg rounded-[24px] bg-white shadow-[0_24px_80px_-24px_rgba(0,0,0,0.45)]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-details-title"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-4 border-b border-black/[0.08] px-5 py-4 sm:px-6">
                  <div>
                    <p
                      className="text-[10px] font-bold uppercase tracking-[0.16em]"
                      style={{ color: BRAND }}
                    >
                      Business details
                    </p>
                    <h2
                      id="edit-details-title"
                      className="mt-1 text-xl font-semibold tracking-[-0.03em] text-black"
                    >
                      Edit contact details
                    </h2>
                    <p className="mt-1 text-xs leading-5 text-black/50">
                      Update the registered address and primary contact email.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeDetailsModal}
                    aria-label="Close edit details"
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-black/50 transition hover:bg-black/[0.05] hover:text-black"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid gap-3 p-5 sm:p-6">
                  <FloatingField
                    id="modalRegisteredAddress"
                    label="Registered address"
                    hint="Start typing and pick an address from the suggestions."
                  >
                    <AddressAutocomplete
                      value={
                        detailsDraft.registeredAddress?.full ||
                        form.registeredAddress ||
                        ''
                      }
                      placeholder="Enter address"
                      disabled={
                        !canEdit ||
                        savingEntity ||
                        entityFields.registered_address?.is_editable === false
                      }
                      showToast={
                        typeof showToast === 'function' ? showToast : undefined
                      }
                      className={autocompleteControlClass}
                      onSelect={(addr) => {
                        if (addr?.full) {
                          lastAddressPickRef.current = Date.now();
                          setDetailsDraft((current) => ({
                            ...current,
                            registeredAddress: addr,
                            addressTouched: true,
                          }));
                          return;
                        }
                        // Ignore a clear that races the pick we just received
                        if (Date.now() - lastAddressPickRef.current < 1500)
                          return;
                        setDetailsDraft((current) => ({
                          ...current,
                          registeredAddress: null,
                          addressTouched: true,
                        }));
                      }}
                    />
                  </FloatingField>

                  <FloatingField
                    id="modalContactEmail"
                    label="Primary contact email"
                  >
                    <input
                      id="modalContactEmail"
                      type="email"
                      value={detailsDraft.contactEmail}
                      onChange={(event) =>
                        setDetailsDraft((current) => ({
                          ...current,
                          contactEmail: event.target.value,
                        }))
                      }
                      readOnly={
                        !canEdit ||
                        entityFields.primary_contact_email?.is_editable ===
                          false
                      }
                      className={controlClass}
                    />
                  </FloatingField>
                </div>

                <div className="flex justify-end gap-2 border-t border-black/[0.08] px-5 py-4 sm:px-6">
                  <button
                    type="button"
                    onClick={closeDetailsModal}
                    disabled={savingEntity}
                    className="inline-flex h-10 items-center rounded-xl px-4 text-sm font-semibold text-black/70 transition hover:bg-black/[0.05] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveDetails}
                    disabled={savingEntity}
                    className="inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ background: BRAND }}
                  >
                    <Save className="h-4 w-4" />
                    {savingEntity ? 'Saving…' : 'Save details'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {createPortal(<div id="mb-datepicker-portal" />, document.body)}
    </div>
  );
}

export default MyBusinessScreen;
